import { useState, useRef, useEffect, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Sparkles, Loader2, AlertCircle, RotateCcw, Check, CheckCircle2, ChevronDown, ChevronRight } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { advisorService } from "../services/advisorService";
import { useResourceCap } from "../hooks/useResourceCap";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { toast } from "sonner";
import { tx } from "../i18n";
// iter-170c — Extracted helpers/hook so ActionButton stays slim
// (CC 39 → ~10) and the field schema is unit-testable.
import {
  actionLabel,
  coerceFieldValue,
  fieldsForActionType,
} from "./advisor/actionFields";
import { useAdvisorAction } from "./advisor/useAdvisorAction";

const SUGGESTIONS_EN = [
  "Give me a 1-month action plan based on my flags.",
  "Make the previous plan more conservative.",
  "What's the single highest-leverage move I can make this week?",
];
const SUGGESTIONS_ES = [
  "Dame un plan de acción de 1 mes basado en mis alertas.",
  "Haz el plan anterior más conservador.",
  "¿Cuál es el movimiento de mayor palanca que puedo hacer esta semana?",
];

// Matches ```lumfi-action ... ``` (multiline JSON) anywhere in the assistant reply.
const ACTION_BLOCK_RE = /```lumfi-action\s*\n([\s\S]*?)```/g;

/**
 * Extracts every well-formed lumfi-action JSON block from a message body and
 * returns:
 *   - prose: the message text with the action blocks stripped
 *   - actions: an array of parsed action objects (best-effort, malformed
 *              blocks are silently dropped so the user still gets the prose).
 */
function extractActions(content) {
  if (!content) return { prose: "", actions: [] };
  const actions = [];
  let m;
  ACTION_BLOCK_RE.lastIndex = 0;
  while ((m = ACTION_BLOCK_RE.exec(content)) !== null) {
    try {
      const obj = JSON.parse(m[1].trim());
      if (obj && typeof obj === "object") actions.push(obj);
    } catch (err) {
      // Malformed lumfi-action block — skip silently so the prose still renders.
      if (process.env.NODE_ENV !== "production") {
        console.warn("malformed lumfi-action JSON", err);
      }
    }
  }
  const prose = content.replace(ACTION_BLOCK_RE, "").trim();
  return { prose, actions };
}

/**
 * Persistent chat with Lumfi AI.
 * - History stored server-side (one thread per user).
 * - Markdown rendering for assistant replies.
 * - Executable actions: assistant can emit ```lumfi-action {json}``` blocks
 *   which render as "Approve" buttons that persist the action via API.
 */
export function AdvisorChat() {
  const { lang } = useLang();
  const qc = useQueryClient();
  const scrollRef = useRef(null);
  const [draft, setDraft] = useState("");
  // iter-189 — Surface monthly AI-messages quota live so the user
  // knows what they have left BEFORE they type. The chip is also
  // refetched after each successful send (the mutation invalidates
  // `billing-my-caps-usage`) so the counter is always fresh.
  const aiCap = useResourceCap("ai_messages_per_month");

  const threadQuery = useQuery({
    queryKey: ["advisor-thread"],
    queryFn: () => advisorService.thread(),
  });
  const messages = threadQuery.data?.messages || [];

  const send = useMutation({
    mutationFn: (text) => advisorService.send(text),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["advisor-thread"] });
      qc.invalidateQueries({ queryKey: ["billing-my-caps-usage"] });
      setDraft("");
      // iter-169z6 — transparent pack debit toast for advisor_chat (matches OCR flow).
      // Only toast when the backend actually decremented a finite grant
      // (paid plans + unlimited grants omit `remaining`).
      if (typeof data?.remaining === "number") {
        const r = data.remaining;
        toast.success(
          tx(lang, `Used 1 AI advisor message · ${r} left`, `Usaste 1 consulta IA · te quedan ${r}`, `Usaste 1 consulta IA · te quedan ${r}`),
        );
      }
    },
    onError: (e) => {
      // iter-189 — Make ``cap_exceeded:ai_messages_per_month`` legible.
      // Previously the user only saw a generic "AI failed" toast. Now
      // we show used/limit and a direct CTA to buy a top-up pack.
      const detail = e?.response?.data?.detail || "";
      if (typeof detail === "string" && detail.startsWith("cap_exceeded:ai_messages_per_month")) {
        const used = aiCap.used ?? "?";
        const limit = aiCap.limit ?? "?";
        toast.error(
          tx(
            lang,
            `Monthly AI messages exhausted (${used}/${limit}). Buy a top-up pack to keep chatting.`,
            `Agotaste tus mensajes IA del mes (${used}/${limit}). Compra un pack para seguir consultando.`,
            `Esgotou seus mensagens IA do mês (${used}/${limit}). Compre um pack para continuar.`,
          ),
          {
            action: {
              label: tx(lang, "View plans", "Ver planes", "Ver planos"),
              onClick: () => { window.location.href = "/billing/my-plan"; },
            },
            duration: 8000,
          },
        );
        return;
      }
      toast.error(tx(lang, "Lumfi AI failed", "Lumfi AI no respondió", "Lumfi AI no respondió"));
    },
  });

  const reset = useMutation({
    mutationFn: () => advisorService.reset(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["advisor-thread"] });
      toast.success(tx(lang, "Conversation reset", "Conversación reiniciada", "Conversación reiniciada"));
    },
  });

  // Auto-scroll to the latest message when a turn lands.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, send.isPending]);

  const submit = (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || send.isPending) return;
    send.mutate(text);
  };

  const suggestions = tx(lang, SUGGESTIONS_EN, SUGGESTIONS_ES, SUGGESTIONS_ES);

  return (
    <Card
      className="mt-5 p-6 rounded-2xl border-slate-200 bg-gradient-to-br from-sky-50 to-white"
      data-testid="advisor-chat"
    >
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-2xl bg-sky-100 text-sky-700 flex items-center justify-center">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-display text-lg font-bold">
              {tx(lang, "Chat with Lumfi AI", "Conversación con Lumfi AI", "Conversación con Lumfi AI")}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5 max-w-xl">
              {tx(lang, "Persistent thread. Iterate, ask for depth, or approve suggested actions with one click.", "Hilo persistente. Itera, pide profundidad, o aprueba acciones sugeridas con un clic.", "Hilo persistente. Itera, pide profundidad, o aprueba acciones sugeridas con un clic.")}
            </p>
          </div>
        </div>
        {messages.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            {/* iter-189 — Quota chip. Color graduates from neutral
                (plenty left) → amber (≥80%) → red (at limit). Click
                opens billing/my-plan for top-up. Hidden for unlimited
                plans to avoid noise. */}
            {!aiCap.loading && !aiCap.unlimited && aiCap.limit > 0 && (
              <Link
                to="/billing/my-plan"
                data-testid="advisor-ai-quota-chip"
                title={tx(lang,
                  "AI messages used this month · click for plans",
                  "Mensajes IA usados este mes · clic para ver planes",
                  "Mensagens IA usadas este mês · clique para ver planos")}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                  aiCap.atHardLimit
                    ? "bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
                    : aiCap.atSoftLimit
                      ? "bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
                      : "bg-sky-50 border-sky-200 text-sky-700 hover:bg-sky-100"
                }`}
              >
                <Sparkles className="h-3 w-3" />
                <span className="tabular-nums">
                  {aiCap.used}/{aiCap.limit}
                </span>
                <span className="opacity-80">
                  {tx(lang, "this month", "este mes", "este mês")}
                </span>
              </Link>
            )}
            <Button
              data-testid="advisor-reset"
              variant="ghost"
              size="sm"
              onClick={() => reset.mutate()}
              disabled={reset.isPending}
              className="text-slate-500 hover:text-red-600 gap-1.5"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {tx(lang, "New conversation", "Nueva conversación", "Nueva conversación")}
            </Button>
          </div>
        )}
        {messages.length === 0 && !aiCap.loading && !aiCap.unlimited && aiCap.limit > 0 && (
          <Link
            to="/billing/my-plan"
            data-testid="advisor-ai-quota-chip"
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
              aiCap.atHardLimit
                ? "bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
                : aiCap.atSoftLimit
                  ? "bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
                  : "bg-sky-50 border-sky-200 text-sky-700 hover:bg-sky-100"
            }`}
          >
            <Sparkles className="h-3 w-3" />
            <span className="tabular-nums">{aiCap.used}/{aiCap.limit}</span>
            <span className="opacity-80">{tx(lang, "this month", "este mes", "este mês")}</span>
          </Link>
        )}
      </div>

      {/* Thread — iter-188: dynamic height so on large screens (≥lg)
          the conversation breathes instead of being cramped to 480px. */}
      <div
        ref={scrollRef}
        data-testid="advisor-thread"
        className="bg-white rounded-xl border border-sky-100 p-4 max-h-[480px] lg:max-h-[60vh] lg:min-h-[480px] xl:min-h-[560px] xl:max-h-[65vh] overflow-y-auto space-y-3"
      >
        {threadQuery.isLoading ? (
          <div className="text-sm text-slate-400 text-center py-8">…</div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8 space-y-3" data-testid="advisor-empty">
            <p className="text-sm text-slate-500">
              {tx(lang, "Say hi to Lumfi AI or try a suggestion 👇", "Saluda a Lumfi AI o prueba una sugerencia 👇", "Saluda a Lumfi AI o prueba una sugerencia 👇")}
            </p>
            <div className="flex flex-col gap-2 max-w-md mx-auto">
              {suggestions.map((s, idx) => (
                <button /* design-system: ALLOW legacy-todo */
                  key={s}
                  type="button"
                  data-testid={`advisor-suggest-${idx}`}
                  onClick={() => send.mutate(s)}
                  disabled={send.isPending}
                  className="text-left text-xs px-3 py-2 rounded-lg border border-sky-200 bg-sky-50 hover:bg-sky-100 text-sky-800 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m) => (
            <ChatBubble key={m.id} message={m} lang={lang} />
          ))
        )}
        {send.isPending && (
          <div className="flex items-center gap-2 text-xs text-sky-600 px-2" data-testid="advisor-typing">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {tx(lang, "Lumfi is thinking…", "Lumfi está pensando…", "Lumfi está pensando…")}
          </div>
        )}
      </div>

      {/* Composer — iter-188: chunkier on lg+ to feel less cramped. */}
      <form onSubmit={submit} className="mt-3 flex gap-2">
        <Input
          data-testid="advisor-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={send.isPending || aiCap.atHardLimit}
          placeholder={aiCap.atHardLimit
            ? tx(lang,
                `Limit reached (${aiCap.used}/${aiCap.limit})`,
                `Límite alcanzado (${aiCap.used}/${aiCap.limit})`,
                `Limite atingido (${aiCap.used}/${aiCap.limit})`)
            : tx(lang, "Write to Lumfi AI…", "Escribe a Lumfi AI…", "Escribe a Lumfi AI…")}
          className="flex-1 lg:h-11 lg:text-base"
        />
        <Button
          type="submit"
          data-testid="advisor-send"
          disabled={!draft.trim() || send.isPending || aiCap.atHardLimit}
          className="bg-sky-600 hover:bg-sky-700 text-white gap-2 lg:h-11 lg:px-5"
        >
          {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {tx(lang, "Send", "Enviar", "Enviar")}
        </Button>
      </form>

      {/* iter-189 — Inline hard-limit notice so the user understands
          why the composer is disabled, with a direct CTA to plans. */}
      {aiCap.atHardLimit && (
        <div
          data-testid="advisor-hard-limit-notice"
          className="mt-2 flex items-start gap-2 p-3 rounded-xl border border-red-200 bg-red-50 text-xs"
        >
          <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-red-800">
              {tx(lang,
                "Monthly AI messages limit reached",
                "Alcanzaste el límite de mensajes IA del mes",
                "Você atingiu o limite de mensagens IA do mês")}
            </div>
            <div className="text-red-700 mt-0.5">
              {tx(lang,
                `${aiCap.used}/${aiCap.limit} used. Upgrade or buy a top-up pack to keep chatting.`,
                `${aiCap.used}/${aiCap.limit} usados. Mejora tu plan o compra un pack para seguir.`,
                `${aiCap.used}/${aiCap.limit} usadas. Atualize seu plano ou compre um pack para continuar.`)}
            </div>
            <Link
              to="/billing/my-plan"
              data-testid="advisor-hard-limit-cta"
              className="inline-flex items-center gap-1 mt-2 px-2.5 py-1 rounded-md bg-white border border-red-200 text-red-700 hover:bg-red-100 transition-colors text-[11px] font-semibold"
            >
              {tx(lang, "View plans", "Ver planes", "Ver planos")}
            </Link>
          </div>
        </div>
      )}

      {send.isError && (
        <div className="mt-2 flex items-center gap-2 text-xs text-red-600">
          <AlertCircle className="h-3.5 w-3.5" />
          {tx(lang, "Failed. Try again.", "Falló. Intenta de nuevo.", "Falló. Intenta de nuevo.")}
        </div>
      )}
    </Card>
  );
}

function ChatBubble({ message, lang }) {
  const isUser = message.role === "user";
  const { prose, actions } = useMemo(
    () => (isUser ? { prose: message.content, actions: [] } : extractActions(message.content)),
    [isUser, message.content]
  );

  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={`advisor-msg-${message.role}`}
    >
      <div
        className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
          isUser
            ? "bg-slate-900 text-white rounded-br-md whitespace-pre-wrap"
            : "bg-sky-50 text-slate-900 rounded-bl-md border border-sky-100"
        }`}
      >
        {isUser ? (
          prose
        ) : (
          <div className="advisor-markdown">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{prose}</ReactMarkdown>
          </div>
        )}
        {!isUser && actions.length > 0 && (
          <div className="mt-3 space-y-2" data-testid={`advisor-actions-${message.id}`}>
            <div className="text-[11px] uppercase tracking-wide text-sky-700 font-semibold">
              {tx(lang, "Suggested actions", "Acciones sugeridas", "Acciones sugeridas")}
            </div>
            {actions.map((act, idx) => (
              <ActionButton key={`${message.id}-${idx}`} action={act} idx={idx} messageId={message.id} lang={lang} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// iter-170c — FIELDS_BY_TYPE + coerceFieldValue now live in
// ./advisor/actionFields.js so the schema can be unit-tested and the
// ActionButton component stays slim.

function ActionField({ keyName, label, type, draft, setDraft, messageId, idx }) {
  const raw = draft[keyName];
  const display = type === "date" && raw ? String(raw).slice(0, 10) : (raw ?? "");
  return (
    <label className="flex flex-col gap-1 text-[11px]">
      <span className="text-slate-600 font-medium">{label}</span>
      <Input
        data-testid={`advisor-action-field-${messageId}-${idx}-${keyName}`}
        type={type}
        step={type === "number" ? "any" : undefined}
        value={display}
        onChange={(e) => setDraft((d) => ({ ...d, [keyName]: coerceFieldValue(type, e.target.value) }))}
        className="h-7 text-xs"
      />
    </label>
  );
}

function ActionApproveButton({ exec, draft, lang, messageId, idx }) {
  return (
    <Button
      type="button"
      size="sm"
      variant="default"
      onClick={() => exec.mutate(draft)}
      disabled={exec.isPending}
      data-testid={`advisor-action-approve-${messageId}-${idx}`}
      className="h-7 px-2.5 text-xs gap-1 bg-sky-600 hover:bg-sky-700 text-white"
    >
      {exec.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
      {tx(lang, "Approve", "Aprobar", "Aprobar")}
    </Button>
  );
}

function ActionButton({ action, idx, messageId, lang }) {
  // iter-170c — Heavy lifting moved into useAdvisorAction hook + helpers
  // in ./advisor/actionFields.js, dropping this component from CC 39 → ~8.
  const t = (en, es, pt) => tx(lang, en, es, pt || es);
  const { done, editing, setEditing, draft, setDraft, exec } = useAdvisorAction({ action, lang });
  const label = actionLabel(action);
  const fields = fieldsForActionType(action.type, t);
  const showFields = editing && !done && fields.length > 0;

  return (
    <div
      className="rounded-lg bg-white border border-sky-200 overflow-hidden"
      data-testid={`advisor-action-${messageId}-${idx}`}
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <button /* design-system: ALLOW legacy-todo */
          type="button"
          onClick={() => !done && setEditing((v) => !v)}
          disabled={done}
          className="text-xs text-slate-700 flex-1 min-w-0 truncate text-left hover:text-sky-700 disabled:cursor-default disabled:hover:text-slate-700"
          data-testid={`advisor-action-toggle-${messageId}-${idx}`}
          title={label}
        >
          <span className="inline-flex items-center gap-1.5">
            {!done && (editing ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />)}
            {label}
          </span>
        </button>
        {done ? (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {t("Done", "Listo")}
          </span>
        ) : (
          <ActionApproveButton exec={exec} draft={draft} lang={lang} messageId={messageId} idx={idx} />
        )}
      </div>
      {showFields && (
        <div
          className="border-t border-sky-100 px-3 py-3 bg-sky-50/40 grid grid-cols-1 sm:grid-cols-2 gap-2"
          data-testid={`advisor-action-fields-${messageId}-${idx}`}
        >
          {fields.map(([key, fLabel, type]) => (
            <ActionField
              key={key}
              keyName={key}
              label={fLabel}
              type={type}
              draft={draft}
              setDraft={setDraft}
              messageId={messageId}
              idx={idx}
            />
          ))}
        </div>
      )}
    </div>
  );
}
